import { alpha, Box, Chip, Divider, Stack, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";

import type { ScoringModel } from "@/apis/argo/scoringModels";
import {
  ModelScoreFilterColumn,
  type ModelScoreFilterInput,
} from "@/generated/company-search";
import { TONE_HEX, tierTone } from "@/lib/scoring/scoreTone";

interface SegmentScoringCardProps {
  /** The V2 models the segment is scored by. */
  models: ScoringModel[];
  /** The segment's V2 score filters, which give each model's kept tiers. */
  modelScoreFilter: ModelScoreFilterInput[] | undefined;
}

/**
 * The tiers a segment keeps for one model, from its tier filters.
 *
 * @param modelScoreFilter - The segment's V2 score filters, if any.
 * @param family - The model's family id.
 * @returns The kept tier letters, empty when the model is not filtered by tier.
 *
 * @example
 * ```typescript
 * tiersOf([{ modelId: "fam-1", column: ModelScoreFilterColumn.TIER, in: ["A"] }], "fam-1");
 * ```
 */
export function tiersOf(
  modelScoreFilter: ModelScoreFilterInput[] | undefined,
  family: string
): string[] {
  return (modelScoreFilter ?? [])
    .filter(
      (filter) =>
        filter.modelId === family &&
        filter.column === ModelScoreFilterColumn.TIER
    )
    .flatMap((filter) => filter.in ?? [])
    .filter((tier): tier is string => typeof tier === "string");
}

/**
 * A kept tier, tinted by its tone band.
 *
 * @param props.family - The model's family id, for the test id.
 * @param props.tier - The tier letter.
 */
function TierChip({ family, tier }: { family: string; tier: string }) {
  const color = TONE_HEX[tierTone(tier)];
  return (
    <Chip
      label={`Tier ${tier}`}
      size="small"
      data-testid={`save-as-workbook-tier-chip-${family}-${tier}`}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        color,
        bgcolor: alpha(color, 0.1),
      }}
    />
  );
}

/**
 * One model the segment is scored by: its name and the tiers it keeps.
 *
 * @param props.model - The model.
 * @param props.tiers - The tiers the segment keeps for it.
 */
function ScoringModelRow({
  model,
  tiers,
}: {
  model: ScoringModel;
  tiers: string[];
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      data-testid={`save-as-workbook-model-${model.family}`}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "purple.main",
          color: "purple.contrastText",
        }}
      >
        <InsertChartOutlinedIcon fontSize="small" />
      </Box>
      <Box minWidth={0}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          data-testid={`save-as-workbook-model-name-${model.family}`}
        >
          {model.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The model applied to this segment
        </Typography>
        {tiers.length > 0 && (
          <Stack direction="row" spacing={0.75} mt={1}>
            {tiers.map((tier) => (
              <TierChip key={tier} family={model.family} tier={tier} />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

/**
 * The scoring a segment's Sales Copilot list keeps: every model the segment
 * is scored by, with its kept tiers, and a note on where their scores show.
 *
 * @param props - See {@link SegmentScoringCardProps}.
 */
export function SegmentScoringCard({
  models,
  modelScoreFilter,
}: SegmentScoringCardProps) {
  const note =
    models.length === 1
      ? "This model will be applied in Sales Copilot. Its scores appear next to the offering scores, which may use different models."
      : "These models will be applied in Sales Copilot. Their scores appear next to the offering scores, which may use different models.";
  return (
    <Box
      data-testid="save-as-workbook-scoring-card"
      sx={(theme) => ({
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: alpha(theme.palette.purple.main, 0.3),
        bgcolor: alpha(theme.palette.purple.main, 0.05),
      })}
    >
      <Stack spacing={2}>
        {models.map((model) => (
          <ScoringModelRow
            key={model.family}
            model={model}
            tiers={tiersOf(modelScoreFilter, model.family)}
          />
        ))}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <CheckIcon fontSize="small" sx={{ color: "purple.main", mt: 0.25 }} />
        <Typography variant="body2" data-testid="save-as-workbook-scoring-note">
          {note}
        </Typography>
      </Stack>
    </Box>
  );
}
